import React, { useState, useCallback } from "react";
import cn from "classnames";
import { Note, Stick, Rest, RestStick } from "./svg";

import Icon from "../../../../icon";
import Tie from "../../score-display/Note/Tie";
import { isMobile } from "../../../../../common/parameters";
import TutorialButton from "../../../Tutorial/Button";
import ScoreTutorial from "../../Tutorial";

import { StyledRhythmPanel, StyledButton, Truplet, ButtonContainer, TieContainer, TutorialWrapper } from "./styles";
import { IProps, IRhytButtonProps, IDotButtonProps, IRemoveButtonProps, ITieButtonProps } from "./interfaces";
import { INote, IScore } from "../../../../../common/types";

const RhytButton = (props: IRhytButtonProps) => {
    const activeTruplet = !!props.score.trupletLeft;
    const { active, children, handleRhythmInput, len, handleTrupletInput, hasTruplet, hint } = props;

    const action = {
        [isMobile ? "onTouchStart" : "onClick"]: (e: MouseEvent | TouchEvent) => {
            handleRhythmInput(Number(len) as INote["l"], e);
        },
    };
    return (
        <ButtonContainer>
            <StyledButton active={active} {...action} className={cn({ pulsate: hint === len })}>
                {children}
            </StyledButton>
            {len === 8 && hasTruplet && (
                <Truplet
                    className={activeTruplet ? "Active" : undefined}
                    onClick={e => {
                        e.stopPropagation();
                        handleTrupletInput();
                    }}
                >
                    <i className="s-truplet" />
                </Truplet>
            )}
        </ButtonContainer>
    );
};

const DotButton = ({ selectedIsDot, handleDotInput }: IDotButtonProps) => {
    const action = {
        [isMobile ? "onTouchStart" : "onClick"]: (e: MouseEvent | TouchEvent) => {
            handleDotInput(e);
        },
    };
    return (
        <StyledButton active={selectedIsDot} {...action}>
            .
        </StyledButton>
    );
};

const RemoveButton = ({ handleRemoveNote, disabled }: IRemoveButtonProps) => {
    const action = {
        [isMobile ? "onTouchStart" : "onClick"]: (e: MouseEvent | TouchEvent) => {
            handleRemoveNote!(e);
        },
    };
    return (
        <StyledButton {...action} disabled={disabled}>
            <Icon i="arrowback" />
        </StyledButton>
    );
};

const TieButton = ({ selectedIsTie, handleTieNote }: ITieButtonProps) => {
    const action = {
        [isMobile ? "onTouchStart" : "onClick"]: handleTieNote,
    };
    return (
        <StyledButton active={selectedIsTie} {...action}>
            <TieContainer>
                <Tie width={28} direction="up" tiedThroughTimeSignature={false} isLastNote={false} barlineType="single"/>
            </TieContainer>
        </StyledButton>
    );
};

const RestToggleButton = ({ handleToggleRest, score }: IProps) => {
    const action = {
        [isMobile ? "onTouchStart" : "onClick"]: (e: MouseEvent | TouchEvent) => {
            handleToggleRest(e);
        },
    };
    return (
        <StyledButton {...action}>
            {score.settings.stick ? (
                <svg width="62" height="62" viewBox="0 0 80 80">
                    <rect x="50.2" y="36.7" width="1.4" height="27.36" />
                    <line x1="58.2" y1="19.3" x2="18.2" y2="59.2" fill="none" strokeWidth="0.89" />
                    <path d="M34.3,34.5a3.4,3.4,0,0,1-3.4,2.4,3.1,3.1,0,0,1-2.6-4.1s-1.8-.7-7.1,3.5h-.6L31.6,17.4a29.3,29.3,0,0,1-5.8,1.1c-1.8,0-3.7-.1-5-1.9a2.9,2.9,0,0,1,.6-4.1h0a5.1,5.1,0,0,1,4.6-.6c1.7.7,1.7,3.3.4,5.3,0,0,2.8,1.2,8.5-3.5L23.3,33.5c.2-.1,3-1.4,4.8-2s3.1-1.5,4.8-.6A2.8,2.8,0,0,1,34.3,34.5Z" />
                </svg>
            ) : (
                <svg width="62" height="62" viewBox="0 0 80 80">
                    <path d="M30.2,37.7c-5.2-4.9-.2-8,3.9-5.8-5.6-7.2-5.5-6.1-1.1-12.6l-6.2-8.6a.7.7,0,0,0-.6.5c3.1,4.2,4.2,5.8-1.1,11,0,0,4.6,5,5.5,6.7,0,0-6-2.6-5.7,3.8.3,2.8,3.1,4.7,5,5.3C30.1,38.1,30.3,37.8,30.2,37.7Z" />
                    <path d="M54.4,32.7V59.9h0c-.1,1.8-1.6,3.9-4,5.1s-6.7,1.4-7.9-.7.5-5.1,3.6-6.8,5.4-1.4,7-.3V32.7Z" />
                    <line
                        x1="58.3"
                        y1="19.1"
                        x2="18.1"
                        y2="59.4"
                        fill="none"
                        strokeMiterlimit="10"
                        strokeWidth="0.89"
                    />
                </svg>
            )}
        </StyledButton>
    );
};

const getUsedRhythms = (score: IScore, editor: boolean) => {
    const buttons = {
        r1: false,
        r2: false,
        r4: false,
        r8: false,
        r16: false,
        dot: false,
        rest: false,
        truplet: false,
    };
    if (editor) {
        return {
            r1: true,
            r2: true,
            r4: true,
            r8: true,
            r16: true,
            dot: true,
            rest: true,
            truplet: true,
        };
    }
    score.bars.forEach(bar => {
        bar?.notes.forEach(note => {
            switch (note.l) {
                case 1:
                    buttons.r1 = true;
                    break;
                case 2:
                    buttons.r2 = true;
                    break;
                case 4:
                    buttons.r4 = true;
                    break;
                case 8:
                    buttons.r8 = true;
                    break;
                case 16:
                    buttons.r16 = true;
                    break;
                default:
                    break;
            }
            if (note.r) {
                buttons.rest = true;
            }
            if (note.d) {
                buttons.dot = true;
            }
            if (note.t) {
                buttons.truplet = true;
            }
        });
    });
    return buttons;
}

const RhythmPanel = (props: IProps) => {
    const { selectedNote, activeVoice, playing, editor, intervalsTask, chords3Task, score } = props;
    const [isShotcutsOpen, setTutorialOpen] = useState(false);

    const toggleTutorialOpen = useCallback(() => {
        setTutorialOpen(!isShotcutsOpen);
    }, [isShotcutsOpen]);

    let Type = Note;
    if (props?.score.settings.stick) Type = Stick;
    if (props.restMode) {
        Type = Rest;
        if (props.score.settings.stick) Type = RestStick;
    }
    let selectedLength = null;
    let selectedIsDot = false;
    let selectedIsTie = false;
    if (
        selectedNote &&
        !selectedNote.lyric &&
        props.score["bars" + (activeVoice || "")][selectedNote.bar]?.notes[selectedNote.note]
    ) {
        selectedLength = props.score["bars" + (activeVoice || "")][selectedNote.bar].notes[selectedNote.note].l;
        selectedIsDot = props.score["bars" + (activeVoice || "")][selectedNote.bar].notes[selectedNote.note].d;
        selectedIsTie = props.score["bars" + (activeVoice || "")][selectedNote.bar].notes[selectedNote.note].tie;
    }

    const usedRhythms = getUsedRhythms(props.taskScore || score, editor);
    return (
        <StyledRhythmPanel style={{ pointerEvents: playing ? "none" : undefined }}>
            {usedRhythms.r1 && (
                <RhytButton {...props} len={1} active={selectedLength === 1}>
                    <Type.note_1 />
                </RhytButton>
            )}
            {!intervalsTask && !chords3Task && (
                <>
                    {usedRhythms.r2 && (
                        <RhytButton {...props} len={2} active={selectedLength === 2}>
                            <Type.note_2 />
                        </RhytButton>
                    )}
                    {usedRhythms.r4 && (
                        <RhytButton {...props} len={4} active={selectedLength === 4}>
                            <Type.note_4 />
                        </RhytButton>
                    )}
                    {usedRhythms.r8 && (
                        <RhytButton {...props} len={8} active={selectedLength === 8} hasTruplet={usedRhythms.truplet}>
                            <Type.note_8 />
                        </RhytButton>
                    )}
                    {usedRhythms.r16 && (
                        <RhytButton {...props} len={16} active={selectedLength === 16}>
                            <Type.note_16 />
                        </RhytButton>
                    )}
                    {usedRhythms.dot && (
                        <DotButton {...props} selectedIsDot={selectedIsDot} />
                    )}
                </>
            )}
            {selectedNote && !selectedNote.lyric ? (
                intervalsTask || chords3Task ? (
                    <RemoveButton disabled handleRemoveNote={() => null}/>
                ) : (
                    <TieButton
                        selectedIsTie={selectedIsTie}
                        handleTieNote={() => {
                            props.handleTieNote(selectedNote.bar, selectedNote.note);
                        }}
                    />
                )
            ) : (
                <RemoveButton {...props} />
            )}
            {!intervalsTask && !chords3Task && usedRhythms.rest && <RestToggleButton {...props} />}
            <TutorialWrapper>{editor && <TutorialButton onClick={toggleTutorialOpen} />}</TutorialWrapper>
            {isShotcutsOpen && <ScoreTutorial close={toggleTutorialOpen} />}
        </StyledRhythmPanel>
    );
};

export default RhythmPanel;
